'use client';

import {
    BoldItalicUnderlineToggles,
    ListsToggle,
    addImportVisitor$,
    headingsPlugin,
    linkDialogPlugin,
    linkPlugin,
    listsPlugin,
    markdownShortcutPlugin,
    MDXEditor,
    realmPlugin,
    toolbarPlugin,
    UndoRedo,
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createHeadingNode } from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import {
    $createParagraphNode,
    $getSelection,
    $isRangeSelection,
} from 'lexical';

import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { AllCapsButton } from './AllCapsButton';
import { ClearFormattingButton } from './ClearFormattingButton';
import { LinkButton } from './LinkButton';
import { TextColorButton } from './TextColorButton';

// Exported so other editors can consume it
export const MARKDOWN_EDITOR_WHITELIST = [
    'Body',
    'Description',
    'DocumentContent',
    'Intro',
    'Header',
    'ServicesHeader',
    'Name',
    'Question',
    'Answer',
    'Tagline',
    'Role',
    'Title',
    'EmailLabel', // label text for email anchor (no link tools)
    'Number',
    'Numbers',
    'Label',
    'Address',
    'BusinessHour',
    'License',
    'Price',
];

/* ----------------------------- helpers ----------------------------- */

function leafOf(key) {
    const k = String(key ?? '');
    return (
        k
            .replace(/\[\d+\]$/, '')
            .split('.')
            .pop() || k
    );
}

// Fields where the link UI must be hidden/disabled (but keep markdown + color)
function isNoLinkLeaf(leaf) {
    const v = String(leaf || '').toLowerCase();
    return (
        v === 'emaillabel' || // hide link tool on label
        v === 'email' ||
        v === 'phone' ||
        v === 'number' ||
        v === 'numbers' ||
        v === 'tel' ||
        v.endsWith('phone') ||
        v.endsWith('phonenumber') ||
        v.endsWith('telephone')
    );
}

function supportsBlockStyleControls(componentName, fieldKey) {
    return (
        String(componentName || '') === 'TermsAndConditions' &&
        leafOf(fieldKey) === 'DocumentContent'
    );
}

function hasChildren(node) {
    return Boolean(node && Array.isArray(node.children));
}

function getInlineStyleAttribute(node) {
    if (
        !node ||
        node.type !== 'mdxJsxTextElement' ||
        !Array.isArray(node.attributes)
    ) {
        return '';
    }

    const styleAttr = node.attributes.find(
        (attr) =>
            attr?.type === 'mdxJsxAttribute' &&
            attr?.name === 'style' &&
            typeof attr?.value === 'string' &&
            attr.value.trim().length > 0
    );

    return styleAttr?.value || '';
}

function hasInlineStyle(node) {
    return Boolean(getInlineStyleAttribute(node));
}

function ensureBoldWeight(styleValue) {
    const clean = String(styleValue || '')
        .trim()
        .replace(/;+\s*$/, '');
    if (!clean) return 'font-weight: 700';
    if (/font-weight\s*:/i.test(clean)) return clean;
    return `${clean}; font-weight: 700`;
}

function normalizeStyledBoldMarkdown(markdown) {
    const input = String(markdown ?? '');
    if (!input.includes('<span') || !input.includes('**')) return input;

    let output = input;

    // MDXEditor exports styled bold as: <span style="...">**Text**</span>
    output = output.replace(
        /<span\s+style=(["'])(.*?)\1>(\s*)\*\*([\s\S]*?)\*\*(\s*)<\/span>/gi,
        (_, __quote, styleValue, leading, inner, trailing) =>
            `<span style="${ensureBoldWeight(
                styleValue
            )}">${leading}${inner}${trailing}</span>`
    );

    // Also normalize HTML-strong variant if present.
    output = output.replace(
        /<span\s+style=(["'])(.*?)\1>\s*<strong>([\s\S]*?)<\/strong>\s*<\/span>/gi,
        (_, __quote, styleValue, inner) =>
            `<span style="${ensureBoldWeight(styleValue)}">${inner}</span>`
    );

    return output;
}

const preserveNestedTextStyleImportPlugin = realmPlugin({
    init(realm) {
        realm.pub(addImportVisitor$, {
            priority: 100,
            testNode: (node) => Boolean(getInlineStyleAttribute(node)),
            visitNode: ({ mdastNode, actions }) => {
                const style = getInlineStyleAttribute(mdastNode);
                if (!style) {
                    actions.nextVisitor();
                    return;
                }

                // Preserve style context through nested formatting wrappers.
                // Required for structures like:
                // <span style="..."><strong>Text</strong></span>
                // <strong style="...">Text</strong>
                actions.addStyle(style, mdastNode);

                const stack = hasChildren(mdastNode)
                    ? [...mdastNode.children]
                    : [];
                while (stack.length > 0) {
                    const child = stack.pop();
                    if (!hasChildren(child)) continue;

                    if (hasInlineStyle(child)) {
                        // Nested styled elements keep their own style scope.
                        continue;
                    }

                    actions.addStyle(style, child);
                    stack.push(...child.children);
                }

                // Let the default visitor chain handle semantics (bold/italic/html).
                actions.nextVisitor();
            },
        });
    },
});

const BLOCK_STYLE_OPTIONS = [
    {
        label: 'Default',
        shortLabel: 'DEFAULT',
        title: 'Default',
        factory: () => $createParagraphNode(),
    },
    {
        label: 'Heading 2',
        shortLabel: 'H2',
        title: 'Heading 2',
        factory: () => $createHeadingNode('h2'),
    },
    {
        label: 'Heading 3',
        shortLabel: 'H3',
        title: 'Heading 3',
        factory: () => $createHeadingNode('h3'),
    },
    {
        label: 'Heading 4',
        shortLabel: 'H4',
        title: 'Heading 4',
        factory: () => $createHeadingNode('h4'),
    },
];

function BlockStyleButtons() {
    const [editor] = useLexicalComposerContext();
    const [selectedValue, setSelectedValue] = useState('');

    return (
        <select
            className="mdx-style-select"
            value={selectedValue}
            onChange={(event) => {
                const nextValue = event.target.value;
                setSelectedValue('');
                const option = BLOCK_STYLE_OPTIONS.find(
                    (item) => item.shortLabel === nextValue
                );
                if (!option) return;

                editor.update(() => {
                    const selection = $getSelection();
                    if (!$isRangeSelection(selection)) return;
                    $setBlocksType(selection, option.factory);
                });
                editor.focus();
            }}
            aria-label="Text styles"
            title="Text styles"
        >
            <option value="">Styles</option>
            {BLOCK_STYLE_OPTIONS.map((option) => (
                <option key={option.shortLabel} value={option.shortLabel}>
                    {option.label}
                </option>
            ))}
        </select>
    );
}

/* ------------------------------ component ------------------------------ */

export function MDEditor(props) {
    const {
        value = '',
        onChange,
        linkValue, // schema-provided link/anchor for this field (hides link UI too)
        hideLinkButton = false,
        disableLinking = false,
        fieldKey, // used to detect Email/Phone/EmailLabel fields
        componentName,
        deferExternalSyncWhileFocused = false,
    } = props;

    const externalMarkdown = useMemo(
        () => normalizeStyledBoldMarkdown(value),
        [value]
    );
    const editorFocusedRef = useRef(false);
    const [deferredMarkdown, setDeferredMarkdown] = useState(externalMarkdown);

    useEffect(() => {
        if (deferExternalSyncWhileFocused && editorFocusedRef.current) return;
        setDeferredMarkdown(externalMarkdown);
    }, [deferExternalSyncWhileFocused, externalMarkdown]);

    const normalizedMarkdown = deferExternalSyncWhileFocused
        ? deferredMarkdown
        : externalMarkdown;

    // schema link present?
    const hasSchemaLink = useMemo(
        () => Boolean(linkValue && String(linkValue).trim()),
        [linkValue]
    );

    // Email/Phone/EmailLabel detection (disable linking but keep markdown & color)
    const isNoLink = useMemo(() => isNoLinkLeaf(leafOf(fieldKey)), [fieldKey]);

    // Effective flags
    const EFFECTIVE_DISABLE_LINKING =
        Boolean(disableLinking) || hasSchemaLink || isNoLink;
    const EFFECTIVE_HIDE_LINK_BTN =
        Boolean(hideLinkButton) || hasSchemaLink || isNoLink;
    const SHOW_BLOCK_STYLE_CONTROLS = useMemo(
        () => supportsBlockStyleControls(componentName, fieldKey),
        [componentName, fieldKey]
    );

    // refs/state
    const linkPanelHostRef = useRef(null);
    const colorPanelHostRef = useRef(null);
    const panelsWrapperRef = useRef(null);
    const editorRef = useRef(null);
    const [openPanel, setOpenPanel] = useState(
        /** @type {null|'link'|'color'} */ (null)
    );
    const [queuedPanel, setQueuedPanel] = useState(
        /** @type {null|'link'|'color'} */ (null)
    );
    const TRANSITION_MS = 200;

    const handleChange = (nextVal) => {
        // Persist immediately so save/deploy never misses the latest edit.
        onChange(normalizeStyledBoldMarkdown(String(nextVal)));
    };

    const syncMarkdownFromEditor = useCallback(() => {
        if (typeof window === 'undefined') return;
        window.requestAnimationFrame(() => {
            const editor = editorRef.current;
            if (!editor?.getMarkdown) return;
            onChange(
                normalizeStyledBoldMarkdown(String(editor.getMarkdown() || ''))
            );
        });
    }, [onChange]);

    useEffect(() => {
        const editor = editorRef.current;
        if (!editor) return;

        const currentMarkdown = normalizeStyledBoldMarkdown(
            editor.getMarkdown?.() || ''
        );

        if (currentMarkdown === normalizedMarkdown) return;
        editor.setMarkdown(normalizedMarkdown);
    }, [normalizedMarkdown]);

    // animated panel host
    useLayoutEffect(() => {
        const wrapper = panelsWrapperRef.current;
        if (!wrapper) return;

        const activeHost =
            openPanel === 'link'
                ? linkPanelHostRef.current
                : openPanel === 'color'
                ? colorPanelHostRef.current
                : null;

        const start = wrapper.offsetHeight;
        const next = activeHost?.firstElementChild
            ? /** @type {HTMLElement} */ (activeHost.firstElementChild)
                  .scrollHeight
            : 0;

        wrapper.style.height = `${start}px`;
        wrapper.style.overflow = 'hidden';
        // force reflow
        // eslint-disable-next-line no-unused-expressions
        wrapper.offsetHeight;

        wrapper.style.transition = `height ${TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`;
        wrapper.style.height = `${next}px`;

        const onEnd = () => {
            wrapper.style.transition = '';
            if (openPanel) {
                wrapper.style.height = '';
                wrapper.style.overflow = 'visible';
            } else {
                wrapper.style.height = '0px';
                wrapper.style.overflow = 'hidden';
            }
            wrapper.removeEventListener('transitionend', onEnd);

            if (!openPanel && queuedPanel) {
                const toOpen = queuedPanel;
                setQueuedPanel(null);
                requestAnimationFrame(() => setOpenPanel(toOpen));
            }
        };

        wrapper.addEventListener('transitionend', onEnd);
        return () => wrapper.removeEventListener('transitionend', onEnd);
    }, [openPanel, queuedPanel]);

    const requestToggle = (kind /* 'link' | 'color' */) => {
        if (openPanel === kind) {
            setQueuedPanel(null);
            setOpenPanel(null);
        } else if (openPanel == null) {
            setOpenPanel(kind);
        } else {
            setQueuedPanel(kind);
            setOpenPanel(null);
        }
    };

    const requestClose = (kind /* 'link' | 'color' */) => {
        if (openPanel === kind) {
            setQueuedPanel(null);
            setOpenPanel(null);
        }
    };

    // Plugins: drop link plugins entirely when linking is disabled
    const plugins = [
        preserveNestedTextStyleImportPlugin(),
        ...(EFFECTIVE_DISABLE_LINKING
            ? []
            : [linkPlugin(), linkDialogPlugin()]),
        headingsPlugin(),
        listsPlugin(),
        markdownShortcutPlugin(),
        toolbarPlugin({
            toolbarContents: () => (
                <div className="w-full">
                    <div className="mdx-toolbar-row mb-[2px] flex items-center gap-3">
                        <UndoRedo />
                        {SHOW_BLOCK_STYLE_CONTROLS && <BlockStyleButtons />}
                        <BoldItalicUnderlineToggles />
                        <ListsToggle options={['bullet', 'number']} />
                        <AllCapsButton />

                        {!EFFECTIVE_HIDE_LINK_BTN &&
                            !EFFECTIVE_DISABLE_LINKING && (
                                <LinkButton
                                    panelHostRef={linkPanelHostRef}
                                    open={openPanel === 'link'}
                                    onRequestToggle={() =>
                                        requestToggle('link')
                                    }
                                    onRequestClose={() => requestClose('link')}
                                    visible={
                                        !EFFECTIVE_HIDE_LINK_BTN &&
                                        !EFFECTIVE_DISABLE_LINKING
                                    }
                                    enabled={!EFFECTIVE_DISABLE_LINKING}
                                    onMarkdownSync={syncMarkdownFromEditor}
                                />
                            )}

                        {/* Keep color tool available always (including email/phone/label) */}
                        <TextColorButton
                            panelHostRef={colorPanelHostRef}
                            open={openPanel === 'color'}
                            onRequestToggle={() => requestToggle('color')}
                            onRequestClose={() => requestClose('color')}
                            onMarkdownSync={syncMarkdownFromEditor}
                        />

                        <ClearFormattingButton
                            onMarkdownSync={syncMarkdownFromEditor}
                        />
                    </div>

                    {/* Panel host(s) */}
                    <div
                        ref={panelsWrapperRef}
                        className="mdx-panel-surface"
                        style={{ height: 0, overflow: 'hidden' }}
                    >
                        {!EFFECTIVE_DISABLE_LINKING && (
                            <div ref={linkPanelHostRef} />
                        )}
                        <div ref={colorPanelHostRef} />
                    </div>
                </div>
            ),
        }),
    ];

    // Block Cmd/Ctrl+K when linking is disabled (email/phone/label or schema)
    const handleKeyDownCapture = (e) => {
        if (!EFFECTIVE_DISABLE_LINKING) return;
        const isMac =
            typeof navigator !== 'undefined' &&
            /Mac|iPhone|iPad/.test(navigator.platform);
        const meta = isMac ? e.metaKey : e.ctrlKey;
        if (meta && (e.key === 'k' || e.key === 'K')) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const handleFocusCapture = () => {
        editorFocusedRef.current = true;
    };

    const handleBlurCapture = (event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        editorFocusedRef.current = false;
        if (deferExternalSyncWhileFocused) {
            setDeferredMarkdown(externalMarkdown);
        }
    };

    return (
        <div
            className="rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600"
            data-editor-flags={`${EFFECTIVE_HIDE_LINK_BTN ? 'hide-link' : ''} ${
                EFFECTIVE_DISABLE_LINKING ? 'no-linking' : ''
            } ${isNoLink ? 'no-link-leaf' : ''}`}
            onKeyDownCapture={handleKeyDownCapture}
            onFocusCapture={handleFocusCapture}
            onBlurCapture={handleBlurCapture}
        >
            <div className="mdxeditor-overrides">
                <MDXEditor
                    ref={editorRef}
                    className="block w-full rounded-sm bg-white text-xs text-gray-900 md:text-sm"
                    contentEditableClassName="mdx-content"
                    markdown={normalizedMarkdown}
                    onChange={handleChange}
                    plugins={plugins}
                />
            </div>

            {/* Scoped styling fixes */}
            <style>{`
.mdxeditor-overrides .mdx-content,
.mdxeditor-overrides [contenteditable="true"] {
  caret-color: #111827 !important;
}

.mdxeditor-overrides .mdx-content {
  min-height: 2.75rem;
}

/* Default link look: don't force underline or bold; let markdown decide */
.mdxeditor-overrides .mdx-content a,
.mdxeditor-overrides a.mdxeditor-link,
.mdxeditor-overrides [data-lexical-link="true"],
.mdxeditor-overrides [contenteditable="true"] a {
  color: var(--primary-700, #1d4ed8) !important;
  text-decoration: none !important;
  font-weight: inherit !important;
}

/* Hover: still no forced underline; underline only if user applied it */
.mdxeditor-overrides .mdx-content a:hover,
.mdxeditor-overrides a.mdxeditor-link:hover,
.mdxeditor-overrides [data-lexical-link="true"]:hover,
.mdxeditor-overrides [contenteditable="true"] a:hover {
  text-decoration: none !important;
  font-weight: inherit !important;
}

.mdxeditor-overrides .mdx-content h2,
.mdxeditor-overrides .mdx-content h3,
.mdxeditor-overrides .mdx-content h4 {
  color: #111827 !important;
  font-family: inherit !important;
  font-weight: 700 !important;
  line-height: 1.25 !important;
  margin: 1rem 0 0.5rem !important;
}

.mdxeditor-overrides .mdx-content h2 {
  font-size: 1.25rem !important;
}

.mdxeditor-overrides .mdx-content h3 {
  font-size: 1.1rem !important;
}

.mdxeditor-overrides .mdx-content h4 {
  font-size: 1rem !important;
}

.mdxeditor-overrides .mdx-content ul,
.mdxeditor-overrides .mdx-content ol {
  margin: 0.75rem 0 !important;
  padding-left: 1.5rem !important;
}

.mdxeditor-overrides .mdx-content ul {
  list-style: disc !important;
}

.mdxeditor-overrides .mdx-content ol {
  list-style: decimal !important;
}

.mdxeditor-overrides .mdx-content li {
  margin: 0.25rem 0 !important;
}

/* No-link-leaf editors (Email/Phone/EmailLabel): allow bold/color but not link styling */
.mdxeditor-overrides [data-editor-flags*="no-link-leaf"] .mdx-content {
  font-weight: 400 !important;
}

.mdxeditor-overrides [data-editor-flags*="no-link-leaf"] .mdx-content a,
.mdxeditor-overrides [data-editor-flags*="no-link-leaf"] a.mdxeditor-link,
.mdxeditor-overrides [data-editor-flags*="no-link-leaf"] [data-lexical-link="true"] {
  font-weight: inherit !important;
  text-decoration: inherit !important;
}

/* Only bold if user explicitly applied strong */
.mdxeditor-overrides [data-editor-flags*="no-link-leaf"] .mdx-content a strong,
.mdxeditor-overrides [data-editor-flags*="no-link-leaf"] .mdx-content strong a {
  font-weight: 600 !important;
}

/* Prevent "inline code" styling around colored text or links */
.mdxeditor-overrides .mdx-content a code,
.mdxeditor-overrides .mdx-content code a,
.mdxeditor-overrides .mdx-content span[style] code,
.mdxeditor-overrides .mdx-content code span[style] {
  font-weight: inherit !important;
  font-style: normal !important;
  font-family: inherit !important;
  background: transparent !important;
  padding: 0 !important;
  border: 0 !important;
}
      `}</style>
        </div>
    );
}
